import { validationsAPI } from '../api/enhancedApiService';
import { getCurrentUTCTimestamp, getMostRecentUTCTimestamp } from '../utils/dateUtils';

class ValidationService {
  // Get validation rules with results
  async getValidations(connectionId, tableName) {
    if (!connectionId || !tableName) {
      throw new Error('Connection ID and table name are required');
    }

    try {
      // Get validation rules
      const rules = await this.getRules(connectionId, tableName);

      // Then get the latest results for these rules
      const results = await this.getLatestResults(connectionId, tableName);

      // Merge rules with results
      return this.mergeRulesWithResults(rules, results);
    } catch (error) {
      console.error('Error fetching validations:', error);
      throw error;
    }
  }

  // Get unified validations (manual + dbt tests)
  async getUnifiedValidations(connectionId, tableName, sourceTypeFilter = null) {
    try {
      const params = new URLSearchParams({
        connection_id: connectionId,
        table: tableName
      });

      if (sourceTypeFilter) {
        params.append('source_type', sourceTypeFilter);
        console.log(`[validationService] Filtering validations by source_type: ${sourceTypeFilter}`);
      } else {
        console.log(`[validationService] No source filter applied (showing all)`);
      }

      const response = await validationsAPI.getUnifiedValidations(params);

      // Handle different API response formats
      let tests = [];
      if (Array.isArray(response)) {
        tests = response;
      } else if (response?.tests) {
        tests = response.tests;
      } else if (response?.data?.tests) {
        tests = response.data.tests;
      }

      // Filter out inactive rules (except auto-cached which need to be toggleable)
      const beforeFilter = tests.length;
      tests = tests.filter(test =>
        test.source_type === 'dbt' || test.source === 'auto_cached' || test.is_active !== false
      );

      console.log(`[validationService] Received ${beforeFilter} tests, filtered to ${tests.length} active tests`);
      if (tests.length > 0) {
        const sources = tests.reduce((acc, t) => {
          const src = t.source || 'unknown';
          acc[src] = (acc[src] || 0) + 1;
          return acc;
        }, {});
        console.log(`[validationService] Source breakdown:`, sources);
      }

      return tests;
    } catch (error) {
      console.error('Error getting unified validations:', error);
      throw error;
    }
  }

  // Get validation rules
  async getRules(connectionId, tableName) {
    try {
      const response = await validationsAPI.getRules(tableName, { connectionId });

      // Handle different API response formats
      let rules = [];
      if (Array.isArray(response)) {
        rules = response;
      } else if (response?.rules) {
        rules = response.rules;
      } else if (response?.data?.rules) {
        rules = response.data.rules;
      }

      // Filter out inactive rules (except auto-cached which need to be toggleable)
      rules = rules.filter(rule => rule.source === 'auto_cached' || rule.is_active !== false);

      return rules;
    } catch (error) {
      console.error('Error getting validation rules:', error);
      throw error;
    }
  }

  // Get latest validation results
  async getLatestResults(connectionId, tableName) {
    try {
      const response = await validationsAPI.getLatestValidationResults(
        connectionId,
        tableName,
        { forceFresh: true } // Add forceFresh option to bypass cache
      );

      // Handle different API response formats
      if (response?.results) {
        return response.results;
      } else if (Array.isArray(response)) {
        return response;
      } else if (response?.data?.results) {
        return response.data.results;
      }

      return [];
    } catch (error) {
      console.error('Error getting latest validation results:', error);
      throw error;
    }
  }

  // Run all validations
  async runValidations(connectionId, tableName, connectionString = null) {
    try {
      console.log(`Running validations for connection ${connectionId}, table ${tableName}`);
      const response = await validationsAPI.runValidations(
        connectionId,
        tableName,
        connectionString  // Pass the connectionString parameter
      );

      // Extract results based on response format and normalize
      let results = [];
      if (response?.results) {
        results = response.results;
      } else if (Array.isArray(response)) {
        results = response;
      } else if (response?.data?.results) {
        results = response.data.results;
      }

      console.log(`Received ${results.length} validation results`);

      // Process the results to ensure they have consistent format
      const processedResults = results.map(result => ({
        rule_name: result.rule_name,
        is_valid: result.is_valid,
        actual_value: result.actual_value,
        expected_value: result.expected_value,
        error: result.error,
        run_at: result.run_at || getCurrentUTCTimestamp(),
        execution_time_ms: result.execution_time_ms,
        // Cost optimization fields
        used_cache: result.used_cache,
        profiling_field: result.profiling_field,
        cost_savings: result.cost_savings
      }));

      return processedResults;
    } catch (error) {
      console.error('Error running validations:', error);
      throw error;
    }
  }

  // Run a single validation
  async runSingleValidation(connectionId, tableName, validationRule) {
    try {
      console.log(`Running single validation for rule: ${validationRule.rule_name}`);
      
      // Use the new single validation endpoint
      const response = await validationsAPI.runSingleValidation({
        table: tableName,
        connection_id: connectionId,
        rule_id: validationRule.id,
        rule_name: validationRule.rule_name
      });

      // Extract result from response
      if (response?.result) {
        return {
          rule_name: validationRule.rule_name,
          is_valid: response.result.is_valid,
          actual_value: response.result.actual_value,
          expected_value: validationRule.expected_value,
          error: response.result.error,
          run_at: getCurrentUTCTimestamp(),
          execution_time_ms: response.result.execution_time_ms,
          // Cost optimization fields
          used_cache: response.result.used_cache,
          profiling_field: response.result.profiling_field,
          cost_savings: response.result.cost_savings
        };
      }

      throw new Error('No result returned from single validation API');
    } catch (error) {
      console.error('Error running single validation:', error);
      throw error;
    }
  }

  // Deactivate a validation rule
  async deactivateRule(connectionId, tableName, ruleName) {
    if (!connectionId || !tableName || !ruleName) {
      throw new Error('Missing required parameters for deactivating rule');
    }

    console.log(`Attempting to deactivate rule "${ruleName}" for table "${tableName}"`);

    try {
      const response = await validationsAPI.deactivateRule(
        tableName,
        ruleName,
        connectionId
      );

      console.log("Deactivation API response:", response);
      return response;
    } catch (error) {
      console.error('Error deactivating rule:', error);
      throw error;
    }
  }

  // Activate a validation rule
  async activateRule(connectionId, tableName, ruleName) {
    if (!connectionId || !tableName || !ruleName) {
      throw new Error('Missing required parameters for activating rule');
    }

    console.log(`Attempting to activate rule "${ruleName}" for table "${tableName}"`);

    try {
      const response = await validationsAPI.activateRule(
        tableName,
        ruleName,
        connectionId
      );

      console.log("Activation API response:", response);
      return response;
    } catch (error) {
      console.error('Error activating rule:', error);
      throw error;
    }
  }

  // Generate default validations
  async generateDefaultValidations(connectionId, tableName) {
    try {
      return await validationsAPI.generateDefaultValidations(
        connectionId,
        tableName
      );
    } catch (error) {
      console.error('Error generating default validations:', error);
      throw error;
    }
  }

  // Helper to merge rules with their results
  mergeRulesWithResults(rules, results) {
    if (!rules || !results) return rules;

    const resultsMap = {};
    results.forEach(result => {
      if (result.rule_name) {
        resultsMap[result.rule_name] = result;
      }
    });

    return rules.map(rule => {
      const result = resultsMap[rule.rule_name];
      if (result) {
        return {
          ...rule,
          last_result: result.error ? null : result.is_valid,
          actual_value: result.actual_value,
          error: result.error,
          last_run_at: result.run_at || getCurrentUTCTimestamp(),
          execution_time_ms: result.execution_time_ms
        };
      }
      return rule;
    });
  }

  // Calculate validation metrics
  calculateMetrics(validations) {
    const total = validations.length;
    const passed = validations.filter(v => v.last_result === true).length;
    const failed = validations.filter(v => v.last_result === false).length;
    const errored = validations.filter(v => v.error).length;
    const notRun = validations.filter(v => v.last_result === undefined || v.last_result === null).length;

    // Calculate health score (only for rules that have been run)
    const runCount = passed + failed;
    const healthScore = runCount > 0 ? Math.round((passed / runCount) * 100) : 0;

    return {
      total,
      passed,
      failed,
      errored,
      notRun,
      healthScore,
      lastRunAt: this.getLastRunTimestamp(validations)
    };
  }

  // Get the most recent run timestamp using UTC utilities
  getLastRunTimestamp(validations) {
    return getMostRecentUTCTimestamp(validations, 'last_run_at');
  }

  // Process validation history into trend data
  processTrendData(history, days = 30) {
    if (!history || !history.length) return [];

    // Group by date
    const byDate = {};

    history.forEach(item => {
      const date = item.run_at ? new Date(item.run_at).toISOString().split('T')[0] : null;
      if (!date) return;

      if (!byDate[date]) {
        byDate[date] = {
          date,
          total: 0,
          passed: 0,
          failed: 0,
          error: 0,
          health_score: 0
        };
      }

      byDate[date].total++;

      if (item.error) {
        byDate[date].error++;
      } else if (item.is_valid === true) {
        byDate[date].passed++;
      } else if (item.is_valid === false) {
        byDate[date].failed++;
      }
    });

    // Calculate health scores and convert to array
    return Object.values(byDate)
      .map(day => {
        const validResults = day.passed + day.failed;
        if (validResults > 0) {
          day.health_score = Math.round((day.passed / validResults) * 100);
        }
        return day;
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }
}

const validationService = new ValidationService();
export default validationService;